import { useEffect, useState } from 'react';

import bigWinSheet from '../../../../../assets/winners-sprites/big-win.json';
import bigWinImage from '../../../../../assets/winners-sprites/big-win.png';
import ccSheet from '../../../../../assets/winners-sprites/cc-winners.json';
import ccImage from '../../../../../assets/winners-sprites/cc-winners.png';
import scSheet from '../../../../../assets/winners-sprites/sc-winners.json';
import scImage from '../../../../../assets/winners-sprites/sc-winners.png';
import type { LoadedSprite, SpriteSheetData } from '../../../../../components/SpriteAnimation/types';

const EMPTY_SPRITE: LoadedSprite = {
  frames: [],
  image: null,
  ready: false,
  frameCount: 0,
};

// Decodes a TexturePacker spritesheet (sort frame keys, extract rects, decode the
// image) into a LoadedSprite. `onReady` is skipped if the load was cancelled.
function loadSprite(sheet: SpriteSheetData, imageUrl: string, onReady: (sprite: LoadedSprite) => void): () => void {
  let cancelled = false;

  const frames = Object.keys(sheet.frames)
    .sort()
    .map((key) => sheet.frames[key].frame);

  const image = new Image();
  image.onload = () => {
    if (!cancelled) onReady({ frames, image, ready: true, frameCount: frames.length });
  };
  image.onerror = () => {
    if (!cancelled) console.error('Failed to load winners sprite:', imageUrl);
  };
  image.src = imageUrl;

  return () => {
    cancelled = true;
  };
}

// Loads the winners sprites once, so the conveyor can share them across all
// animated tiles instead of loading per tile: the jackpot coin-burst sprites
// (sweeps + social) plus the single shared big-win sprite (both coin types).
export function useWinnerSprites(): { scSprite: LoadedSprite; ccSprite: LoadedSprite; bigWinSprite: LoadedSprite } {
  const [scSprite, setScSprite] = useState<LoadedSprite>(EMPTY_SPRITE);
  const [ccSprite, setCcSprite] = useState<LoadedSprite>(EMPTY_SPRITE);
  const [bigWinSprite, setBigWinSprite] = useState<LoadedSprite>(EMPTY_SPRITE);

  useEffect(() => {
    const cancelSc = loadSprite(scSheet as SpriteSheetData, scImage, setScSprite);
    const cancelCc = loadSprite(ccSheet as SpriteSheetData, ccImage, setCcSprite);
    const cancelBigWin = loadSprite(bigWinSheet as SpriteSheetData, bigWinImage, setBigWinSprite);

    return () => {
      cancelSc();
      cancelCc();
      cancelBigWin();
    };
  }, []);

  return { scSprite, ccSprite, bigWinSprite };
}
